import { useEffect, useRef, useState } from 'react';
import { getDefaultLanguageLabel, sortLanguageOptionsForDisplay } from '@minibook/shared';
import { useTranslation } from '../../i18n/useTranslation';
import { getLanguageMode, resetToAutoLanguageMode } from '../../i18n/config';

export function LanguageSwitcher() {
  const { t, language, languages, changeLanguage } = useTranslation();
  const [open, setOpen] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const languageMode = getLanguageMode();
  const currentLanguage = language.split('-')[0] || 'en';
  const selectedValue = languageMode === 'auto' ? 'auto' : currentLanguage;
  const sortedOptions = sortLanguageOptionsForDisplay(
    languages.map((lng) => ({
      value: lng,
      label: getDefaultLanguageLabel(lng),
    })),
    languageMode === 'auto' ? currentLanguage : selectedValue,
    currentLanguage
  );

  const triggerLabel = currentLanguage.toUpperCase();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const run = async (fn: () => Promise<void>) => {
    setIsPending(true);
    try {
      await fn();
    } finally {
      setIsPending(false);
    }
  };

  const handleAuto = () => {
    void run(async () => {
      await resetToAutoLanguageMode();
      setOpen(false);
    });
  };

  const handlePick = (code: string) => {
    if (code === currentLanguage && languageMode === 'manual') {
      setOpen(false);
      return;
    }
    void run(async () => {
      if (languageMode === 'manual' && code === currentLanguage) return;
      await changeLanguage(code);
      setOpen(false);
    });
  };

  return (
    <div
      ref={rootRef}
      className={`booking-lang-switcher${open ? ' is-open' : ''}`}
      aria-label={t('booking.languageSelectAria')}
    >
      <button
        type="button"
        className="booking-lang-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={isPending}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="booking-lang-label">{triggerLabel}</span>
        <span className="booking-lang-caret" aria-hidden>
          ▾
        </span>
      </button>
      <div className="booking-lang-menu" role="listbox">
        <button
          type="button"
          role="option"
          className={`booking-lang-option${languageMode === 'auto' ? ' is-active' : ''}`}
          onClick={handleAuto}
        >
          {t('booking.autoLanguageLabel')}
        </button>
        {sortedOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            role="option"
            className={`booking-lang-option${
              languageMode === 'manual' && option.value === currentLanguage ? ' is-active' : ''
            }`}
            onClick={() => handlePick(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}
